/**
 * Shack — header menu hover behaviour
 *
 * Prestige's MenuDisclosure hardcodes `mouseOverDelayTolerance` to 250ms before closing on mouseout, and
 * MegaMenuDisclosure then fades the panel for a further ~0.4s. For that whole window the panel is still
 * rendered and still hit-testable, and `mouseover` bubbles up from it — so moving back over the fading
 * panel re-fires the open handler and the menu reopens without the pointer ever reaching the header.
 *
 * This sets the tolerance to the configured value (0 by default) and makes the panel non-interactive while
 * it closes, so reopening requires hovering the header item again.
 *
 * It also adds a hover-intent delay BEFORE opening, which the theme has no concept of: MenuDisclosure's
 * hover handler calls `toggle(true)` the instant `mouseover` fires, so panels flew open whenever the
 * pointer merely crossed the header on its way somewhere else. Note this is a separate thing from
 * `mouseOverDelayTolerance`, which only governs CLOSING.
 *
 * The open delay applies only from a COLD menu bar. Once any panel is open, hover intent has already been
 * demonstrated, so moving sideways to another top-level item switches instantly — Jordan on 86d3yjdtz:
 * "if moving sideways across the menu can we have no delay? ... with the delay it seems slow when going
 * from menu item to menu item". The countdown still guards the first open, which is the case it was added
 * for: a pointer merely crossing the header on its way somewhere else. Set data-instant-switch="false" to
 * restore the delay on every open.
 *
 * The open is delayed by wrapping `toggle`. Whether a given toggle came from hover or from a click is
 * decided by document-level capture listeners, which run before the element's own handlers: a capture
 * `mouseover` records which disclosure the pointer is over, and `pointerdown` clears it so clicking a menu
 * item still opens immediately. `mouseover` bubbles from descendants and therefore fires repeatedly while
 * the pointer moves inside one item, so a countdown already in flight is never restarted; the matching
 * `mouseout` cancels it, but only once the pointer has genuinely left the disclosure (relatedTarget test)
 * rather than merely crossed between its children.
 *
 * Configured from Theme settings > Navigation via data attributes on the script tag, so the original
 * behaviour can be restored without a code change.
 *
 * Equal mega menu heights are handled purely in CSS (see --mega-menu-min-height in custom.css) — no
 * measurement here. An earlier version measured the tallest menu from detached clones and published it as a
 * min-height, which broke the menus: min-height wins over the theme's max-height, so an unclamped measured
 * value forced every panel taller than the viewport.
 */
(function () {
  if (window.__shackHeaderMenuBehaviour) return;
  window.__shackHeaderMenuBehaviour = true;

  var script = document.currentScript;
  var closeDelay = parseInt((script && script.dataset.closeDelay) || '0', 10) || 0;
  var openDelay = parseInt((script && script.dataset.openDelay) || '0', 10) || 0;
  var blockReopenWhileClosing = (script && script.dataset.blockReopen) !== 'false';
  var instantSwitch = (script && script.dataset.instantSwitch) !== 'false';

  var DISCLOSURE_SELECTOR = 'mega-menu-disclosure, dropdown-menu-disclosure';

  // The disclosure the pointer is currently over, or null when the last interaction was a click.
  // Set from capture-phase listeners so it is always up to date before the theme's own hover handler
  // (and therefore before the toggle wrapper below) runs.
  var hoveredDisclosure = null;

  function closestDisclosure(node) {
    return node && node.closest ? node.closest(DISCLOSURE_SELECTOR) : null;
  }

  // True when a sibling panel is already open, i.e. the menu bar is 'warm'. Hover intent has already
  // been demonstrated in that case, so a sideways move to another top-level item should switch
  // instantly rather than sitting through the countdown again (86d3yjdtz).
  function anotherDisclosureOpen(self) {
    var nodes = document.querySelectorAll(DISCLOSURE_SELECTOR);

    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i] === self) continue;
      if (nodes[i].disclosureElement && nodes[i].disclosureElement.open) return true;
    }

    return false;
  }

  function cancelPendingOpen(el) {
    if (el && el.__shackOpenTimer) {
      clearTimeout(el.__shackOpenTimer);
      el.__shackOpenTimer = null;
    }
  }

  document.addEventListener(
    'mouseover',
    function (event) {
      hoveredDisclosure = closestDisclosure(event.target);
    },
    true
  );

  document.addEventListener(
    'mouseout',
    function (event) {
      var el = closestDisclosure(event.target);
      if (!el) return;
      // mouseout also fires when moving between descendants of the same disclosure; only a move that
      // genuinely leaves it should cancel a countdown.
      if (event.relatedTarget && el.contains(event.relatedTarget)) return;
      cancelPendingOpen(el);
      if (hoveredDisclosure === el) hoveredDisclosure = null;
    },
    true
  );

  // A click must open straight away, so forget the hover context before the theme handles it.
  document.addEventListener(
    'pointerdown',
    function () {
      hoveredDisclosure = null;
    },
    true
  );

  function patchDisclosure(tagName) {
    if (!window.customElements) return;

    window.customElements.whenDefined(tagName).then(function () {
      var ctor = window.customElements.get(tagName);
      if (!ctor || ctor.__shackPatched) return;
      ctor.__shackPatched = true;

      var proto = ctor.prototype;

      Object.defineProperty(proto, 'mouseOverDelayTolerance', {
        configurable: true,
        get: function () {
          return closeDelay;
        }
      });

      if (openDelay > 0 && typeof proto.toggle === 'function') {
        var originalToggle = proto.toggle;

        proto.toggle = function (force, animate) {
          var self = this;
          var isHoverOpen = force === true && hoveredDisclosure === self;
          var alreadyOpen = self.disclosureElement && self.disclosureElement.open;
          var warmSwitch = instantSwitch && anotherDisclosureOpen(self);

          if (isHoverOpen && !alreadyOpen && !warmSwitch) {
            // Already counting down for this item — let it finish rather than restarting on every
            // mouseover that bubbles up from a descendant.
            if (self.__shackOpenTimer) return;

            self.__shackOpenTimer = setTimeout(function () {
              self.__shackOpenTimer = null;
              // The pointer may have left during the countdown; mouseout clears the timer, so reaching
              // here means it is still inside.
              originalToggle.call(self, true, animate);
            }, openDelay);

            return;
          }

          cancelPendingOpen(self);
          return originalToggle.apply(self, arguments);
        };
      }

      if (!blockReopenWhileClosing) return;

      var originalOpen = proto.open;
      var originalClose = proto.close;

      proto.close = function () {
        var content = this.contentElement;
        if (content) content.style.pointerEvents = 'none';
        return originalClose.apply(this, arguments);
      };

      proto.open = function () {
        var content = this.contentElement;
        if (content) content.style.removeProperty('pointer-events');
        return originalOpen.apply(this, arguments);
      };
    });
  }

  patchDisclosure('mega-menu-disclosure');
  patchDisclosure('dropdown-menu-disclosure');
})();
